import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import StudentReceiptModal from './StudentReceiptModal';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Spin,
  message,
  Row,
  Col,
  Statistic,
  Alert,
  Layout
} from 'antd';
import {
  NumberOutlined,
  DollarOutlined,
  CreditCardOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './MyPerformance.css'
import getUserIdFromToken from '../../Utils/authUtils';

const { Text } = Typography;
const { Content } = Layout;

function StudentBilling() {
  const { t } = useTranslation();
  const userId = getUserIdFromToken();
  const [billingData, setBillingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receiptModalVisible, setReceiptModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  const API_URL = process.env.REACT_APP_API_URL;


  

const fetchStudentBilling = useCallback(async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      message.error(t('No token found. Please log in.'));
      return;
    }

    if (!userId) {
      message.error(t('Student ID not found. Please log in again.'));
      return;
    }

    const url = `${API_URL}/students/${userId}/payments`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const cleaned = (response.data || []).map((item, index) => {
      const parsedClasses = typeof item.classes === 'string'
        ? (() => {
            try {
              return JSON.parse(item.classes || '[]');
            } catch (e) {
              console.error(`❌ Error parsing item.classes at index ${index}:`, e);
              return [];
            }
          })()
        : (Array.isArray(item.classes) ? item.classes : []);

      const parsedInventory = typeof item.inventory_items === 'string'
        ? (() => {
            try {
              return JSON.parse(item.inventory_items || '[]');
            } catch (e) {
              console.error(`❌ Error parsing item.inventory_items at index ${index}:`, e);
              return [];
            }
          })()
        : (Array.isArray(item.inventory_items) ? item.inventory_items : []);

      return {
        ...item,
        classes: parsedClasses,
        inventory_items: parsedInventory
      };
    });

    setBillingData(cleaned);
  } catch (error) {
    console.error('❌ Error fetching billing data:', error);
    message.error(
      t('Error fetching billing history: ') +
      (error.response?.data?.message || t('Server error'))
    );
  } finally {
    setLoading(false);
  }
}, [API_URL, userId, t]);


useEffect(() => {
  fetchStudentBilling();
}, [fetchStudentBilling]);

const summaryStats = useMemo(() => {
  if (!billingData || billingData.length === 0) {
    return {
      totalPaid: 0,
      totalRemaining: 0,
      totalTransactions: 0,
      lastPayment: null
    };
  }

  const totalPaid = billingData.reduce((sum, payment) => {
    const payAmount = parseFloat(payment.pay_amount || 0);
    return sum + payAmount;
  }, 0);

  // Deduplicate remaining balance by batch_id
  const latestByBatch = {};
  billingData.forEach(payment => {
    const key = payment.batch_id || payment.payment_id;
    const current = latestByBatch[key];
    const newTime = new Date(payment.last_payment_date);
    if (!current || newTime > new Date(current.last_payment_date)) {
      latestByBatch[key] = payment;
    }
  });

  const totalRemaining = Object.values(latestByBatch).reduce((sum, payment) => {
    const remaining = parseFloat(payment.remaining_balance || 0);
    return remaining > 0 ? sum + remaining : sum;
  }, 0);

  const totalTransactions = billingData.length;
  const lastPayment = billingData[0] || null;

  return {
    totalPaid,
    totalRemaining,
    totalTransactions,
    lastPayment
  };
}, [billingData]);

  const handleViewReceipt = (payment) => {
    setSelectedPayment(payment);
    setReceiptModalVisible(true);
  };

  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return <DollarOutlined />;
      case 'credit card':
      case 'card':
        return <CreditCardOutlined />;
      default:
        return <DollarOutlined />;
    }
  };

  const getPaymentMethodColor = (method) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return 'green';
      case 'credit card':
      case 'card':
        return 'blue';
      case 'bank transfer':
        return 'purple';
      default:
        return 'default';
    }
  };

  const columns = [
    {
  title: t('receipt'),
  dataIndex: 'receipt_no',
  key: 'receipt_no',
  width: 120,
  render: (text, record) => (
    <Button
      type="primary"
      size="small"
      icon={<NumberOutlined />}
      onClick={() => handleViewReceipt(record)}
    >
      {text || t('receipt')}
    </Button>
  ),
},
    {
      title: t('date'),
      dataIndex: 'last_payment_date',
      key: 'last_payment_date',
      width: 120,
      render: (last_payment_date) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs(last_payment_date).format('MMM DD, YYYY')}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(last_payment_date).format('HH:mm')}
          </Text>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.last_payment_date).unix() - dayjs(b.last_payment_date).unix(),
    },
   {
  title: t('courses'),
  dataIndex: 'classes',
  key: 'courses',
  width: 240,
  render: (classes) => {
    if (!Array.isArray(classes) || classes.length === 0) {
      return <Text type="secondary">N/A</Text>;
    }

    return (
      <div>
        {classes.map((cls, index) => (
          <div key={index} style={{ marginBottom: 4 }}>
            <Tag color="blue" style={{ marginBottom: 2 }}>{cls.class_code}</Tag><br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              ฿{parseFloat(cls.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              {cls.total_hours ? ` • ${(cls.total_hours / 60).toFixed(1)} hrs` : ''}
            </Text>
          </div>
        ))}
      </div>
    );
  }
},
{
  title: t('discount'),
  dataIndex: 'discount',
  key: 'discount',
  width: 100,
  render: (discount) => (
    <Text style={{ color: 'red' }}>
      -฿{parseFloat(discount || 0).toLocaleString()}
    </Text>
  ),
},
{
  title: t('vat'),
  dataIndex: 'vat',
  key: 'vat',
  width: 100,
  render: (vat) => (
    <Text>
      ฿{parseFloat(vat || 0).toLocaleString()}
    </Text>
  ),
},
    {
      title: t('final_price'),
      dataIndex: 'final_price',
      key: 'final_price',
      width: 120,
      render: (finalPrice) => (
        <Text>฿{parseFloat(finalPrice || 0).toLocaleString()}</Text>
      ),
    },
    {
      title: t('amount_paid'),
      dataIndex: 'pay_amount',
      key: 'pay_amount',
      width: 120,
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          ฿{parseFloat(amount || 0).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => parseFloat(a.pay_amount || 0) - parseFloat(b.pay_amount || 0),
    },
    {
      title: t('remaining'),
      dataIndex: 'remaining_balance',
      key: 'remaining_balance',
      width: 140,
      render: (balance) => {
        const amount = parseFloat(balance || 0);
        return (
          <Text style={{ color: amount > 0 ? '#ff4d4f' : '#52c41a' }}>
            ฿{amount.toLocaleString()}
          </Text>
        );
      },
      sorter: (a, b) => parseFloat(a.remaining_balance || 0) - parseFloat(b.remaining_balance || 0),
    },
    {
      title: t('paymentMethod'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 130,
      render: (method) => (
        <Tag color={getPaymentMethodColor(method)} icon={getPaymentMethodIcon(method)}>
          {method || 'N/A'}
        </Tag>
      ),
      filters: [
        { text: 'Cash', value: 'cash' },
        { text: 'Credit Card', value: 'credit card' },
        { text: 'Bank Transfer', value: 'bank transfer' },
      ],
      onFilter: (value, record) => record.payment_method?.toLowerCase().includes(value),
    }
  ];



  return (
    <div>
      <Sidebar />
      <Header />

      <Content className="performance-content">
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t('Total Paid')}
                value={summaryStats.totalPaid}
                prefix="฿"
                valueStyle={{ color: '#52c41a' }}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t('Outstanding Balance')}
                value={summaryStats.totalRemaining}
                prefix="฿"
                valueStyle={{ color: summaryStats.totalRemaining > 0 ? '#ff4d4f' : '#52c41a' }}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t('Total Transactions')}
                value={summaryStats.totalTransactions}
                prefix={<NumberOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t('Last Payment')}
                value={summaryStats.lastPayment ? dayjs(summaryStats.lastPayment.last_payment_date).format('MMM DD, YYYY') : 'N/A'}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Outstanding Balance Alert */}
        {summaryStats.totalRemaining > 0 && (
          <Alert
            message={t('Outstanding Balance')}
            description={`You have an outstanding balance of ฿${summaryStats.totalRemaining.toLocaleString()}. Please contact the office for payment arrangements.`}
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        {/* Billing History Table */}
        <Card>
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={billingData}
              rowKey="payment_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} transactions`,
              }}
              scroll={{ x: 'max-content' }}
              style={{ width: '100%', overflowX: 'auto' }}
              locale={{
                emptyText: loading ? 'Loading...' : 'No billing history found'
              }}
            />
          </Spin>
        </Card>

        {/* Receipt Modal */}
        <StudentReceiptModal
          visible={receiptModalVisible}
          onClose={() => setReceiptModalVisible(false)}
          paymentData={selectedPayment}
        />
      </Content>
    </div>
  );
}

export default withRoleAccess(StudentBilling, ['student']);